/**
 * AudioEngine - single shared AudioContext with iOS self-healing
 *
 * iOS/WebKit kills Web Audio in ways most code never checks for:
 * - contexts enter a non-standard 'interrupted' state after screen lock,
 *   backgrounding, calls, Siri, alarms — and WebKit doesn't auto-resume
 * - contexts can return from background claiming state === 'running' while
 *   their render thread is dead (frozen currentTime "zombie") — only a
 *   close() + rebuild inside a real user gesture fixes that
 * - the OS can refuse to activate the page's audio session entirely
 *   (fresh contexts born as zombies, resume() rejecting with "Failed to
 *   start the audio device") — only claiming a 'playback' session fixes that
 *
 * Every sound consumer must get its context from getAudioCtx() and never
 * cache it across calls (or re-key caches on the context instance).
 */

let _ctx = null
let _needsReset = false // a gesture-time resume verifiably failed → rebuild
let _lastTime = 0       // ctx.currentTime sampled at the previous gesture
let _lastStamp = 0      // Date.now() of that sample (0 = no sample yet)

/* ---------------------------------------------------------------- */
/* Debug log — persisted so it survives force-closes; the           */
/* ?audiodebug=1 overlay renders it. audioLog() calls are permanent */
/* and cheap; the overlay is the only reader.                       */
/* ---------------------------------------------------------------- */

const LOG_KEY = 'audioEngineLog'
const LOG_MAX = 300
let _log = []
const _logListeners = new Set()

try {
  const saved = localStorage.getItem(LOG_KEY)
  if (saved) _log = JSON.parse(saved)
} catch { /* private mode or corrupt entry */ }

export function audioLog(message) {
  const stamp = new Date().toISOString().slice(11, 23)
  _log.push(`${stamp} ${message}`)
  if (_log.length > LOG_MAX) _log = _log.slice(-LOG_MAX)
  try { localStorage.setItem(LOG_KEY, JSON.stringify(_log)) } catch { /* full/private */ }
  _logListeners.forEach(fn => { try { fn() } catch { /* listener error */ } })
}

export function getAudioLog() {
  return _log
}

export function clearAudioLog() {
  _log = []
  try { localStorage.removeItem(LOG_KEY) } catch { /* private mode */ }
  _logListeners.forEach(fn => { try { fn() } catch { /* listener error */ } })
}

export function subscribeAudioLog(fn) {
  _logListeners.add(fn)
  return () => _logListeners.delete(fn)
}

/** Snapshot of engine internals for the debug overlay (poll it — state
 *  changes don't trigger React re-renders). */
export function getAudioEngineState() {
  return {
    ctxState: _ctx ? _ctx.state : 'none',
    currentTime: _ctx ? _ctx.currentTime : 0,
    sampleRate: _ctx ? _ctx.sampleRate : 0,
    sessionState: (typeof navigator !== 'undefined' && navigator.audioSession)
      ? navigator.audioSession.state
      : 'unsupported',
    sessionType: (typeof navigator !== 'undefined' && navigator.audioSession)
      ? navigator.audioSession.type
      : 'unsupported',
    needsReset: _needsReset
  }
}

/* ---------------------------------------------------------------- */
/* Root Cause 4: claim a high-priority 'playback' audio session.    */
/* Without it iOS may decline to (re)activate the default ambient   */
/* session — every context is then born a zombie (state 'running',  */
/* clock frozen at 0.000) and resume() rejects with "Failed to      */
/* start the audio device". iOS may reset the session type across   */
/* backgrounding, so this runs before every context creation too.   */
/* NOTE: a 'playback' session plays through the ring/silent switch. */
/* ---------------------------------------------------------------- */

function claimPlaybackSession() {
  const session = typeof navigator !== 'undefined' ? navigator.audioSession : null
  if (!session) return
  try {
    if (session.type !== 'playback') {
      session.type = 'playback'
      audioLog(`audioSession type set to playback (state=${session.state})`)
    }
  } catch { /* older WebKit without settable type */ }
}

export function getAudioCtx() {
  if (!_ctx || _ctx.state === 'closed') {
    claimPlaybackSession()
    try {
      _ctx = new (window.AudioContext || window.webkitAudioContext)()
    } catch (err) {
      audioLog(`ctx creation FAILED: ${err && err.message}`)
      _ctx = null
      return null
    }
    _needsReset = false
    _lastStamp = 0
    audioLog(`ctx created: state=${_ctx.state} sr=${_ctx.sampleRate}`)
    _ctx.addEventListener('statechange', function onState() {
      // this === the context the listener was added to, so logs stay
      // attributed correctly even after a rebuild
      audioLog(`statechange → ${this.state}`)
    })
  }
  if (_ctx.state !== 'running') resumeCtx(_ctx)
  return _ctx
}

// resume() from any non-running state ('suspended' OR iOS 'interrupted').
function resumeCtx(ctx) {
  try {
    const p = ctx.resume()
    if (p && typeof p.catch === 'function') {
      p.catch(err => audioLog(`resume() REJECTED: ${err && err.message}`))
    }
  } catch { /* context closed mid-call */ }
}

// Silent one-sample buffer so iOS registers real playback on unlock.
function kick(ctx) {
  try {
    const src = ctx.createBufferSource()
    src.buffer = ctx.createBuffer(1, 1, ctx.sampleRate)
    src.connect(ctx.destination)
    src.start(0)
  } catch { /* context closed mid-call */ }
}

if (typeof document !== 'undefined') {
  claimPlaybackSession()

  const session = typeof navigator !== 'undefined' ? navigator.audioSession : null
  if (session && typeof session.addEventListener === 'function') {
    session.addEventListener('statechange', () => {
      audioLog(`audioSession state → ${session.state}`)
    })
  }

  const unlock = () => {
    const now = Date.now()

    if (_ctx && _ctx.state !== 'closed') {
      // Zombie check: 'running' but the clock hasn't moved since a sample
      // taken >700 ms ago means the render thread is dead. A live context
      // advances currentTime every ~3 ms, so this cannot false-positive.
      const zombie =
        _ctx.state === 'running' &&
        _lastStamp !== 0 &&
        now - _lastStamp > 700 &&
        _ctx.currentTime === _lastTime
      if (_needsReset || zombie) {
        audioLog(`gesture: HARD RESET (${zombie ? `zombie: clock frozen at ${_lastTime.toFixed(3)}` : 'flagged by failed resume'})`)
        try { _ctx.close() } catch { /* already closed */ }
        _ctx = null // rebuilt below, inside this gesture's call stack
      }
    }

    const ctx = getAudioCtx() // creates and/or resumes as needed
    if (!ctx) return

    if (ctx.state !== 'running') {
      kick(ctx)
      // Verify the resume took; if the context is still stuck shortly
      // after a genuine gesture, only a rebuild will fix it.
      const target = ctx
      setTimeout(() => {
        if (target === _ctx && target.state !== 'running' && !document.hidden) {
          _needsReset = true
          audioLog(`resume verification failed: state=${target.state} → flagged for reset`)
        }
      }, 400)
    }

    _lastTime = ctx.currentTime
    _lastStamp = now
  }
  // Capture phase: runs before any framework handler in the same gesture.
  document.addEventListener('pointerdown', unlock, true)
  document.addEventListener('touchend', unlock, true)
  document.addEventListener('keydown', unlock, true)

  // Best-effort resume when returning to the app outside a gesture
  // (iOS doesn't always fire visibilitychange when returning via the
  // app switcher, hence pageshow and focus too).
  const resumeIfVisible = () => {
    if (!document.hidden && _ctx && _ctx.state !== 'running' && _ctx.state !== 'closed') {
      resumeCtx(_ctx)
    }
  }
  document.addEventListener('visibilitychange', resumeIfVisible)
  window.addEventListener('pageshow', resumeIfVisible)
  window.addEventListener('focus', resumeIfVisible)
}
